import { inject, Injector } from '@angular/core';
import { catchError, filter, tap, throwError } from 'rxjs';
import {  HttpEvent, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';

/**
 * libs
 */
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';


/**
 * utilities
 */
import { AppUtils } from '@core/utilities';
import { AppHttpUtils, IResponse } from '../../../utilities';

/**
 * models
 */
import { AppHttpErrorResponse } from '../../../models/httpErrorResponse/app-http-error-response';

/**
 * interfaces
 */
import { IToastMsgHttpContext } from './interfaces/toast-msg-http-context';

/**
 * toastr interceptor
 * @param req - The HTTP request
 * @param next - The next interceptor in the chain
 * @returns {Observable<HttpEvent<any>>} The HTTP response
 * @description Handles toastr messages for HTTP requests.
 * Returns the HTTP response with toastr messages.
 * ```
 */
export const toastrInterceptor: HttpInterceptorFn = (req, next) => {
  
  // check if request has bypass toast context
  if (AppHttpUtils.isHasBypassToast(req) || !AppHttpUtils.isAppRequest(req)) {
    return next(req);
  }
  
  // injects
  const _toast = inject(ToastrService);
  const _injector = inject<Injector>(Injector);

  return next(req).pipe(
    filter((event: HttpEvent<any>) => event instanceof HttpResponse),
    tap((event: HttpResponse<any>) => {

      // get translate service
      const _translate = _injector.get(TranslateService);

      // get method
      const method = req.method.toUpperCase();
      // get content
      const _content = toastContentHandler(req, event, _translate);
      // if content is not null, return
      if (_content == null) return;

      // ES6 Destructuring
      const { type, title, message } = _content;

      // if type is not null, show toast with type
      if (type != null)  {
        _toast.show(message, title, undefined, type);
      }
      else {
        // if type is null, show toast with method
        switch(method) {
          case 'POST':
            _toast.success(message, title);
            break;
          case 'PUT':
            _toast.info(message, title);
            break;
          case 'DELETE':
            _toast.warning(message, title);
            break;
        }
      }

    }),
    catchError((_response: AppHttpErrorResponse) => {

      // get translate service
      const _translate = _injector.get(TranslateService);
      
      // if method is GET and has toast force context, return error
      if (req.method.toUpperCase() === 'GET' && !AppHttpUtils.isHasToastForce(req)) {
        return throwError(() => _response);
      }

      // get errors
      const _message = _response.singleErrorMessage;

      // show toast error
      _toast.error(_message ?? _translate.instant('HTTP.ERROR.FAILED'));

      return throwError(() => _response);
    })
  )
};

/**
 * Toast content handler
 * @param req - The HTTP request
 * @param event - The HTTP response
 * @param _translate - The translate service
 * @returns {IToastMsgHttpContext | null} The toast content
 * @description Handles toast content for HTTP requests.
 * Returns the toast content for HTTP requests.
 * ```
 * ```typescript
 * const toastContent = toastContentHandler(req, event, _translate);
 * ```
 */
export function toastContentHandler(
  req: HttpRequest<unknown>, 
  event: HttpResponse<unknown>,
  _translate: TranslateService
): IToastMsgHttpContext | null {

  // get context config
  const _contextConfig = AppHttpUtils.getToastMessages(req.context);
  let { message, title, i18nParams,  type = undefined } = _contextConfig ?? {};

  // get body
  const _body = event.body as IResponse<unknown> | null;
  const _data = _body?.data;

  // no message was asked for: take the server's own wording, either as a bare
  // data string or out of the field the request named
  if (message == null) {

    const _messageKey = AppHttpUtils.getToastMessageKey(req.context);

    if (typeof _data === 'string') {
      message = _data;
    }
    else if (_messageKey != null && _data != null && typeof _data === 'object') {
      // the key may be a single field or a dot-path into nested data
      const _keyed = AppUtils.chainByReducerFn(_messageKey, _data as Record<string, unknown>);
      if (typeof _keyed === 'string') message = _keyed;
    }
  }

  if (title != null) {
    title = _translate.instant(title, i18nParams);
  }

  if (message != null) {
    message = _translate.instant(message, i18nParams);
  }
  
  // return content
  const _content = { type, title, message };

  if (Object.values(_content).every(value => value == null)) return null;
 
  return _content as IToastMsgHttpContext;
  
}
